import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, CircularProgress, Typography, Grid, Card, CardContent, TextField, Button
} from '@mui/material';
import ModuleHub from '../components/ModuleHub';
import {
  getAccountingUsers, getAccountingRoles, getAuditLogs, getAccountingLoginHistory,
  getAccountingSettings, updateAccountingSettings, seedAccountingPermissions,
  getAccountingPermissions, createAccountingRole
} from '../../../services/api';

const UserAccessManagement = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roleDialog, setRoleDialog] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadAll(); }, [tab]);

  const loadAll = async () => {
    try {
      setLoading(true);
      if (tab === 0) { const r = await getAccountingUsers(); setUsers(r.data || []); }
      if (tab === 1) {
        const [rolesRes, permRes] = await Promise.all([getAccountingRoles(), getAccountingPermissions()]);
        setRoles(rolesRes.data || []);
        setPermissions(permRes.data || []);
        await seedAccountingPermissions().catch(() => {});
      }
      if (tab === 2) { const r = await getAuditLogs(); setAuditLogs(r.data || []); }
      if (tab === 3) { const r = await getAccountingLoginHistory(); setLoginHistory(r.data || []); }
      if (tab === 4) { const r = await getAccountingSettings(); setSettings(r.data || {}); }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await updateAccountingSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <ModuleHub moduleId="user-access" showFeatures={false}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Users" />
        <Tab label="Roles & RBAC" />
        <Tab label="Audit Trail" />
        <Tab label="Login History" />
        <Tab label="Security Policies" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Settings saved successfully</Alert>}
      {loading ? <CircularProgress /> : (
        <>
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead><TableRow><TableCell>Username</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell></TableRow></TableHead>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>{u.full_name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell><Chip label={u.role} size="small" color="primary" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {tab === 1 && (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={() => setRoleDialog(true)}>Add Role</Button>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {roles.map(role => (
                  <Grid item xs={12} sm={6} md={4} key={role.id}>
                    <Card><CardContent>
                      <Typography variant="h6">{role.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{role.description}</Typography>
                      {role.is_system && <Chip label="System Role" size="small" sx={{ mt: 1 }} />}
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="subtitle1" gutterBottom>Permission Matrix ({permissions.length} permissions)</Typography>
              <TableContainer component={Paper}><Table size="small">
                <TableHead><TableRow><TableCell>Module</TableCell><TableCell>Action</TableCell><TableCell>Description</TableCell></TableRow></TableHead>
                <TableBody>{permissions.slice(0, 20).map(p => (
                  <TableRow key={p.id}><TableCell>{p.module}</TableCell><TableCell>{p.action}</TableCell><TableCell>{p.description}</TableCell></TableRow>
                ))}</TableBody>
              </Table></TableContainer>
              {roleDialog && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>New Role</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><TextField fullWidth label="Role Name" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} /></Grid>
                    <Grid item xs={6}><TextField fullWidth label="Description" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} /></Grid>
                    <Grid item xs={12}>
                      <Button variant="contained" sx={{ mr: 1 }} onClick={async () => { await createAccountingRole(roleForm); setRoleDialog(false); setRoleForm({ name: '', description: '' }); loadAll(); }}>Save Role</Button>
                      <Button onClick={() => setRoleDialog(false)}>Cancel</Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>User</TableCell><TableCell>Action</TableCell><TableCell>Entity</TableCell></TableRow></TableHead>
                <TableBody>
                  {auditLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell>{log.user_name || 'System'}</TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.entity_type} #{log.entity_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {tab === 3 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>User ID</TableCell><TableCell>IP</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {loginHistory.map(h => (
                    <TableRow key={h.id}>
                      <TableCell>{new Date(h.created_at).toLocaleString()}</TableCell>
                      <TableCell>{h.user_id}</TableCell>
                      <TableCell>{h.ip_address}</TableCell>
                      <TableCell><Chip label={h.login_status} size="small" color={h.login_status === 'success' ? 'success' : 'error'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {tab === 4 && (
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth type="number" label="Password Min Length" value={settings.password_min_length || 8} onChange={(e) => setSettings({ ...settings, password_min_length: parseInt(e.target.value) })} /></Grid>
                <Grid item xs={6}><TextField fullWidth type="number" label="Session Timeout (minutes)" value={settings.session_timeout_minutes || 30} onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })} /></Grid>
                <Grid item xs={6}><TextField fullWidth type="number" label="Max Login Attempts" value={settings.max_login_attempts || 5} onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })} /></Grid>
                <Grid item xs={6}><TextField fullWidth type="number" label="Lockout Duration (minutes)" value={settings.lockout_duration_minutes || 30} onChange={(e) => setSettings({ ...settings, lockout_duration_minutes: parseInt(e.target.value) })} /></Grid>
                <Grid item xs={12}><Button variant="contained" onClick={saveSettings}>Save Security Policies</Button></Grid>
              </Grid>
            </Paper>
          )}
        </>
      )}
    </ModuleHub>
  );
};

export default UserAccessManagement;
